import { MODULE_SETTING, businessModules, moduleOn, type BusinessModule } from "@teamna/shared";
import { FormSection, InlineError, ToggleRow, showToast } from "../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useOwnerBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { useUpdateBusiness } from "@teamna/web-client/hooks/use-setup.ts";

/**
 * Settings, Modules (2026-11-03): the parts of Teamna an owner or an admin turns on or off. Every
 * module is off until they turn it on (`moduleOn` in `packages/shared/src/business-modules.ts`).
 * The switch saves at once with `PATCH /owner/businesses/:id`, like the other switches in Settings.
 */
type ModuleWords = { title: string; hint: string; on: string; off: string };

export function ModulesSettings({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const business = useOwnerBusiness(businessId);
  const update = useUpdateBusiness(businessId);
  const staff = label("staff", { plural: true });
  const people = label("participant", { plural: true });

  const words: Record<BusinessModule, ModuleWords> = {
    coaches_add_participants: {
      title: `${label("staff", { plural: true, capital: true })} add their ${people}`,
      hint: `Each ${label("staff")} types the names of the ${people} they teach, under each ${label("group")}. Then you link the ${label("member", { plural: true })}. On: the Staff page shows the card that asks ${staff} to do it. Use it when you start with Teamna, and turn it off when the names are in.`,
      on: `${label("staff", { plural: true, capital: true })} can add ${people} now.`,
      off: `Only you add ${people} now.`,
    },
  };

  return (
    <FormSection title="Modules" description="Turn on the parts of Teamna your business uses. Every module is off until you turn it on.">
      {businessModules.map((module) => (
        <ToggleRow
          key={module}
          label={words[module].title}
          hint={words[module].hint}
          checked={moduleOn(business.data?.settings, module)}
          disabled={update.isPending || !business.data}
          onChange={(next) =>
            update.mutate(
              { settings: { [MODULE_SETTING[module]]: next } },
              { onSuccess: () => showToast(next ? words[module].on : words[module].off) },
            )
          }
        />
      ))}
      {update.error ? <InlineError error={update.error} /> : null}
    </FormSection>
  );
}
